import { QueryClient, QueryKey } from "@tanstack/react-query";

import { FileSystemNode } from "@/shared/domain/files/filesystem";

import { DirectoryQueryKeyParams, fileQueryKeys } from "./fileQueryKeys";

export type DirectoryContentsCache = {
  items: FileSystemNode[];
  nextToken: string | undefined;
};

type CacheSnapshot = [QueryKey, DirectoryContentsCache | undefined][];

/** Parent directory path. Returns "" for top-level paths (dataset root). */
export function dirPath(path: string): string {
  const idx = path.lastIndexOf("/");
  return idx === -1 ? "" : path.slice(0, idx);
}

/** Last segment of a path (filename or directory name). */
export function baseName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/**
 * Cancels all in-flight directoryContents queries and returns a snapshot of
 * the cache entries for the given directories, for use in onError rollback.
 */
export async function cancelAndSnapshotDirectoryContents(
  queryClient: QueryClient,
  datasetId: string,
  directoryPaths: string[],
): Promise<CacheSnapshot> {
  await queryClient.cancelQueries({
    queryKey: fileQueryKeys.directoryContents(),
    exact: false,
  });

  const all = queryClient.getQueriesData<DirectoryContentsCache>({
    queryKey: fileQueryKeys.directoryContents(),
    exact: false,
  });

  return all.filter(([key]) => matchesDir(key, datasetId, directoryPaths));
}

/** Restores cache entries from a snapshot taken by cancelAndSnapshotDirectoryContents. */
export function restoreDirectoryContentsSnapshot(
  queryClient: QueryClient,
  snapshot: CacheSnapshot,
) {
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}

/**
 * Applies `transform` to the items array of every cached directoryContents
 * entry matching `datasetId` + `directoryPath`.
 */
export function transformDirectoryContentsCache(
  queryClient: QueryClient,
  datasetId: string,
  directoryPath: string,
  transform: (items: FileSystemNode[]) => FileSystemNode[],
) {
  const all = queryClient.getQueriesData<DirectoryContentsCache>({
    queryKey: fileQueryKeys.directoryContents(),
    exact: false,
  });

  for (const [key, data] of all) {
    if (!data || !matchesDir(key, datasetId, [directoryPath])) {
      continue;
    }
    queryClient.setQueryData<DirectoryContentsCache>(key, {
      ...data,
      items: transform(data.items),
    });
  }
}

function matchesDir(
  key: QueryKey,
  datasetId: string,
  paths: string[],
): boolean {
  const params = (key as unknown[])[3] as DirectoryQueryKeyParams | undefined;
  return (
    params?.datasetId === datasetId &&
    paths.includes(params?.directoryPath ?? "")
  );
}
